import {getTestProps} from '@benchling/js-test-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState, useEffect, useRef, useMemo} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {ChatInput, useChatInputControls} from 'llm/chat-library/ChatInput';
import {ChatMessageThread} from 'llm/chat-library/ChatMessageThread';
import ChatScrollContainer from 'llm/chat-library/ChatScrollContainer';
import ComposeOverEntryBanner from 'llm/chat-library/ComposeOverEntryBanner';
import {getAgentWorkspaceFolderId} from 'llm/chat-library/getAgentWorkspaceFolderId';
import {getSelfScheduledState} from 'llm/chat-library/getSelfScheduledState';
import {getTargetItem} from 'llm/chat-library/getTargetItem';
import {getChatLayout} from 'llm/chat-library/layouts/ChatLayoutRegistry';
import LegacyAgentDeprecationBanner from 'llm/chat-library/LegacyAgentDeprecationBanner';
import SelfScheduledBanner from 'llm/chat-library/SelfScheduledBanner';
import {
  doesAgentTypeSupportInterrupts,
  doesAssistantDeprecateAgent,
  doesChatTypeShowProgress,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useArtifacts} from 'llm/chat-library/useArtifacts';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import {LatestUserMessageInfo} from 'llm/ChatScrollTypes';
import {DraftIndicatorButton} from 'llm/data-capture/DraftIndicatorButton';
import {ProcedureToolMappingProvider} from 'llm/procedure-tool-library/ProcedureToolMappingContext';
import notifications from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {ButtonStyle} from 'ui-library/buttons/Button';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import * as modals from 'util/Modals';

export interface ChatProps {
  chatSessionId: string;
  chatType: ChatType;
  isExpanded: boolean;
  locked?: boolean;
}

/**
 * Generic chat component that supports multiple chat types (Deep Research, Compose, etc.).
 *
 * This component uses the artifact abstraction to manage outputs like draft documents,
 * and can show progress UI for chat types that support tool calls.
 */
export function Chat({chatSessionId, chatType, isExpanded, locked = false}: ChatProps): JSX.Element {
  const controls = useChatInputControls(chatSessionId);
  const {agentType, agentTypeError, isPending} = controls;
  const [hasTrackedChatOpen, setHasTrackedChatOpen] = useState<boolean>(false);
  const [waitingForArtifactBeforeSave, setWaitingForArtifactBeforeSave] = useState<boolean>(false);

  // Persist scroll position across sidepanel expand/collapse remounts of ChatScrollContainer.
  // -1 means no saved position; 0+ is a valid scrollTop.
  const savedScrollPositionRef = useRef<number>(-1);

  // Treat pending + agentTypeError as a failed submit state rather than active submission.
  const isSubmitting = waitingForArtifactBeforeSave || (isPending && agentTypeError == null);

  const {
    events,
    isLoading,
    errorMessage,
    isThreadError,
    isAssistantRunning,
    isLastMessageFromUser,
    isSubmitErrorMessage,
    sendMessage,
    canInterruptAssistant,
    isInterrupting,
    interruptAssistant,
    progressData,
    citationData,
    latestApplicationState,
    chatSessionDisplayName,
  } = useMessageThread({
    chatSessionId,
    includeToolCalls: doesChatTypeShowProgress(chatType),
    disableRealtimeUpdates: locked,
  });
  const [latestUserMessageInfo, setLatestUserMessageInfo] = useState<LatestUserMessageInfo | null>(null);

  const {llmRouteCoScientistToUnified} = useRolloutFlags();
  const routeCoScientistToUnified = llmRouteCoScientistToUnified === true;

  const showStopButton =
    ((canInterruptAssistant && isAssistantRunning) || isInterrupting) &&
    !isSubmitErrorMessage &&
    agentType != null &&
    doesAgentTypeSupportInterrupts(agentType, routeCoScientistToUnified);

  const handleInterrupt = useCallback(async () => {
    if (!showStopButton || isInterrupting) {
      return;
    }
    await interruptAssistant();
  }, [interruptAssistant, isInterrupting, showStopButton]);

  // Use the artifacts hook - handles document and source file artifacts from application state
  const artifacts = useArtifacts({
    latestApplicationState,
    isExpanded,
    chatType,
    chatSessionId,
  });
  const {primaryArtifact, isPhoneArtifactOpen, openArtifact, closePhoneArtifact} = artifacts;

  // On phone-sized screens, we show the chat by default and have an option to open the artifact.
  const isPhoneWidth = useIsPhoneWidth();

  const toolCalls = useMemo(() => citationData?.toolCalls ?? [], [citationData]);

  const targetItem = getTargetItem(agentType, latestApplicationState);
  const selfScheduledState = getSelfScheduledState(agentType, latestApplicationState);
  const agentWorkspaceFolderId = getAgentWorkspaceFolderId(agentType, latestApplicationState);
  const hasAgentTypeError = agentTypeError != null;

  // Track opening of AI chat (only once per session)
  useEffect(() => {
    if (!isLoading && !hasTrackedChatOpen) {
      // TODO(BNCH-133493): openedNewChat is always false - also this should be invoked for all chat types.
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.OPENED_LLM_CHAT, {
        chatType,
        messageCount: events.filter((event) => event.__typename === 'DisplayMessageChatEventNode').length,
        openedNewChat: Boolean(!chatSessionId),
      });
      setHasTrackedChatOpen(true);
    }
  }, [chatSessionId, chatType, isLoading, hasTrackedChatOpen, events]);

  const handleSubmitMessage = useCallback(async () => {
    if (!agentType) {
      // This is unexpected, but just in case, notify the user why their message was not sent.
      notifications.error('An agent type must be selected before sending a message', {
        id: 'chat-agent-type-required',
      });
      return;
    }

    // If we need to retry, show a confirmation modal
    if (isLastMessageFromUser) {
      const confirmed = await modals.confirm(
        'Alert',
        'The previous message encountered an error and will be removed. Do you want to continue?',
        ButtonStyle.PRIMARY,
        {
          acceptText: 'Continue',
          rejectText: 'Cancel',
          promiseCompatible: true,
          enableRedesign: true,
        }
      );

      if (!confirmed) {
        return;
      }
    }

    // Wait for artifact saves before sending
    if (primaryArtifact?.beforeSendMessage) {
      setWaitingForArtifactBeforeSave(true);
      try {
        await primaryArtifact.beforeSendMessage();
      } finally {
        setWaitingForArtifactBeforeSave(false);
      }
    }

    // Draft state already has everything - just submit
    sendMessage({retry: isLastMessageFromUser});
  }, [primaryArtifact, agentType, sendMessage, isLastMessageFromUser]);

  // If we have an error message, we may or may not have any past chat history to show. If there is
  // no past chat history (e.g. error on initial pageload), just show an error for the whole chat.
  // Otherwise, we will render the events we have below with a staleness message.
  if (errorMessage != null && events.length === 0) {
    return (
      <div
        className={css(styles.errorBannerContainer)}
        {...getTestProps({component: 'Chat', element: 'error-banner-container'})}
      >
        <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
      </div>
    );
  }

  const showLegacyAgentDeprecationBanner =
    BenchlingConfig.LLM_SHOW_LEGACY_DEPRECATION_BANNER &&
    agentType != null &&
    doesAssistantDeprecateAgent(agentType);

  const isComposeOverEntry =
    agentType ===
      (BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT ? AgentType.ASSISTANT : AgentType.DATA_CAPTURE) &&
    targetItem != null;

  const messageThreadElement =
    // events may be non-empty while loading due to optimistic messages
    events.length === 0 && (isLoading || isAssistantRunning) ? (
      <CenteredHorizontalThrobber {...getTestProps({component: 'Chat', element: 'loading-throbber'})} />
    ) : (
      <ChatMessageThread
        events={events}
        chatSessionId={chatSessionId}
        chatSessionDisplayName={chatSessionDisplayName}
        onUpdateLatestUserMessageInfo={setLatestUserMessageInfo}
        banner={isComposeOverEntry ? <ComposeOverEntryBanner /> : null}
        actionButton={
          primaryArtifact ? (
            <DraftIndicatorButton onClick={openArtifact} artifactType={primaryArtifact.artifactType} />
          ) : null
        }
        readonly={locked}
        progressData={progressData}
        citationData={citationData}
      />
    );

  const chatContent = (
    <ChatScrollContainer
      chatMessagesElement={messageThreadElement}
      chatInputElement={
        locked ? null : (
          <ChatInput
            chatSessionId={chatSessionId}
            {...controls}
            onSubmit={handleSubmitMessage}
            showStopButton={showStopButton}
            onInterrupt={handleInterrupt}
            isInterrupting={isInterrupting}
            inputDisabled={
              (isAssistantRunning && !isSubmitErrorMessage) ||
              (isSubmitting && !isSubmitErrorMessage) ||
              (errorMessage != null && !hasAgentTypeError && !isThreadError)
            }
            allowedChatTypes={[chatType]}
            agentWorkspaceFolderId={agentWorkspaceFolderId}
            targetItem={targetItem}
          />
        )
      }
      latestUserMessageInfo={latestUserMessageInfo}
      autoScroll={true}
      savedScrollPositionRef={savedScrollPositionRef}
    />
  );

  const isEditable =
    (!isAssistantRunning || isSubmitErrorMessage) && !locked && (!isSubmitting || isSubmitErrorMessage);

  // Get the appropriate layout for this chat type
  const LayoutComponent = getChatLayout(chatType);

  let content;
  if (isPhoneWidth) {
    // Mobile: show chat by default, or artifact when the user has opened it.
    if (isPhoneArtifactOpen && primaryArtifact) {
      const ArtifactComponent = primaryArtifact.Component;
      // TODO: ideally the component with the close is wrapping the artifact, i.e.
      // not a concern of a given artifact component.
      content = (
        <ArtifactComponent data={primaryArtifact.data} editable={isEditable} onClose={closePhoneArtifact} />
      );
    } else {
      content = chatContent;
    }
  } else if (isExpanded) {
    // Desktop expanded view: use layout component (handles artifacts internally)
    content = <LayoutComponent chatContent={chatContent} artifacts={artifacts} isEditable={isEditable} />;
  } else {
    // Desktop collapsed view: show chat only
    content = chatContent;
  }

  return (
    <div className={css(styles.contentContainer)}>
      {showLegacyAgentDeprecationBanner && (
        <div className={css(styles.infoBannerContainer)}>
          <LegacyAgentDeprecationBanner />
        </div>
      )}
      {selfScheduledState != null && !isAssistantRunning && (
        <div className={css(styles.infoBannerContainer)}>
          <SelfScheduledBanner selfScheduledState={selfScheduledState} />
        </div>
      )}
      {errorMessage != null && !isThreadError && !isSubmitting && !isAssistantRunning && (
        <div
          className={css(styles.errorBannerContainer)}
          {...getTestProps({component: 'Chat', element: 'error-banner-with-chat'})}
        >
          <InlineBanner
            bannerType={InlineBannerType.ERROR}
            text={`${errorMessage} Showing previously-loaded chat.`}
          />
        </div>
      )}
      <ProcedureToolMappingProvider toolCalls={toolCalls}>{content}</ProcedureToolMappingProvider>
    </div>
  );
}

const styles = StyleSheet.create({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minHeight: 0,
    flex: 1,
  },
  errorBannerContainer: {
    margin: '16px 16px 0',
  },
  infoBannerContainer: {
    margin: '16px 16px 0',
  },
});
